import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Filter } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { STAGES } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import MeetingsReport from "@/pages/reports/MeetingsReport";
import QuotationsReport from "@/pages/reports/QuotationsReport";
import { downloadFile } from "@/lib/download";
import { toast } from "sonner";

function isoDay(d) { return d.toISOString().slice(0, 10); }
function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: isoDay(start), to: isoDay(now) };
}

function ReportBuilder({ users, isAdmin }) {
  const [kind, setKind] = useState("won_deals");
  const [range, setRange] = useState(defaultRange);
  const [stage, setStage] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [customerType, setCustomerType] = useState("");

  const REPORT_KINDS = [
    { v: "won_deals", l: "Won deals" },
    { v: "deals", l: "All deals" },
    { v: "followups", l: "Follow-ups" },
    { v: "clients", l: "Clients" },
    { v: "activities", l: "Activity log" },
    ...(isAdmin ? [{ v: "leaderboard", l: "Sales leaderboard" }] : []),
  ];

  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    const params = { kind };
    if (range.from) params.from_date = new Date(range.from).toISOString();
    if (range.to) params.to_date = new Date(range.to + "T23:59:59").toISOString();
    if (stage && (kind === "deals")) params.stage = stage;
    if (ownerId && isAdmin) params.owner_id = ownerId;
    if (customerType && kind === "clients") params.customer_type = customerType;
    setDownloading(true);
    try {
      // Direct in-page download — no new tab/window, lands straight in the
      // browser's download manager.
      await downloadFile("/reports/export", params, `renviro_${kind}.csv`);
    } catch {
      toast.error("Download failed — please try again");
    } finally { setDownloading(false); }
  };

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mb-6" data-testid="report-builder">
      <CardContent className="p-4 sm:p-6">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3 flex items-center gap-1.5"><Filter className="h-3 w-3" /> Build a downloadable report</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="rounded-2xl h-10 mt-1" data-testid="report-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                {REPORT_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">From</Label>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="rounded-2xl h-10 mt-1" data-testid="report-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">To</Label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="rounded-2xl h-10 mt-1" data-testid="report-to" />
          </div>
          {kind === "deals" && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Stage</Label>
              <Select value={stage || "any"} onValueChange={(v) => setStage(v === "any" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="any">Any stage</SelectItem>
                  {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === "clients" && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Customer type</Label>
              <Select value={customerType || "any"} onValueChange={(v) => setCustomerType(v === "any" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="any">Any type</SelectItem>
                  <SelectItem value="new_lead">New lead</SelectItem>
                  <SelectItem value="existing">Existing</SelectItem>
                  <SelectItem value="repeat">Repeat</SelectItem>
                  <SelectItem value="amc">AMC</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdmin && kind !== "leaderboard" && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Salesperson</Label>
              <Select value={ownerId || "any"} onValueChange={(v) => setOwnerId(v === "any" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-10 mt-1" data-testid="report-owner"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="any">All salespeople</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2 sm:col-span-3 xl:col-span-1 flex items-end">
            <Button onClick={download} disabled={downloading} className="rounded-2xl h-10 tap w-full" data-testid="report-download">
              <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Preparing…" : "Download CSV"}
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">Tip: open the CSV in Excel for instant pivot tables. Date range covers when the record was last updated (deals) / due (follow-ups) / created (clients & activities).</p>
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const canTeam = (user?.permissions || []).includes("view_team_reports");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      const tasks = [api.get("/reports/summary")];
      if (canTeam) tasks.push(api.get("/reports/team").catch(() => ({ data: [] })));
      if (isAdmin) tasks.push(api.get("/users").catch(() => ({ data: [] })));
      const out = await Promise.all(tasks);
      setData(out[0].data);
      let i = 1;
      if (canTeam) { setTeam(out[i].data || []); i++; }
      if (isAdmin) { setUsers((out[i].data || []).filter((x) => x.is_active)); }
      setLoading(false);
    })();
  }, [canTeam, isAdmin]);

  if (loading || !data) return <div className="p-6"><Skeleton className="h-12 w-1/3 mb-4" /><Skeleton className="h-96 rounded-3xl" /></div>;

  const maxStage = Math.max(...Object.values(data.by_stage).map((v) => v.value || 0), 1);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Reports</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Pipeline pulse.</h1>
      </header>

      <ReportBuilder users={users} isAdmin={isAdmin} />

      {isAdmin && <QuotationsReport users={users} />}

      {isAdmin && <MeetingsReport />}

      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        {[
          { l: "Leads value", v: fmtINR(data.leads_value), c: "text-primary-700" },
          { l: "RFQ value", v: fmtINR(data.rfq_value), c: "text-amber-600" },
          { l: "Won", v: fmtINR(data.won_value), s: `${data.won_count} deals`, c: "text-emerald-600" },
          { l: "Conversion", v: `${data.conversion_pct}%`, s: "won / total deals", c: "text-foreground" },
        ].map((s) => (
          <Card key={s.l} className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{s.l}</p>
              <p className={`mt-2 font-display text-2xl sm:text-3xl font-black ${s.c}`}>{s.v}</p>
              {s.s && <p className="text-xs text-muted-foreground">{s.s}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Pipeline by stage (value)</p>
          <div className="space-y-3">
            {STAGES.map((s) => {
              const v = data.by_stage[s.key] || { count: 0, value: 0 };
              const pct = (v.value / maxStage) * 100;
              return (
                <div key={s.key} className="flex items-center gap-3" data-testid={`stage-bar-${s.key}`}>
                  <span className="w-40 text-xs font-semibold text-muted-foreground capitalize">{s.label}</span>
                  <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-28 text-right font-display font-extrabold text-sm">{fmtINR(v.value)}</span>
                  <span className="w-10 text-right text-xs text-muted-foreground">{v.count}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Pipeline by industry (value)</p>
          {(!data.by_industry || data.by_industry.length === 0) ? (
            <p className="py-6 text-center text-xs text-muted-foreground">No deals to break down by industry yet.</p>
          ) : (
            <div className="space-y-3" data-testid="industry-breakdown">
              {(() => {
                const maxInd = Math.max(...data.by_industry.map((r) => r.value || 0), 1);
                return data.by_industry.slice(0, 12).map((r) => {
                  const pct = (r.value / maxInd) * 100;
                  const winPct = r.count ? Math.round((r.won_count / r.count) * 100) : 0;
                  return (
                    <div key={r.industry} className="flex items-center gap-3" data-testid={`industry-row-${r.industry}`}>
                      <span className="w-44 text-xs font-semibold text-muted-foreground truncate" title={r.industry}>{r.industry}</span>
                      <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-28 text-right font-display font-extrabold text-sm">{fmtINR(r.value)}</span>
                      <span className="w-12 text-right text-xs text-muted-foreground">{r.count}</span>
                      <span className={`w-12 text-right text-xs font-semibold ${winPct >= 50 ? "text-emerald-700" : winPct >= 25 ? "text-amber-700" : "text-muted-foreground"}`} title={`${r.won_count}/${r.count} won`}>
                        {winPct}%
                      </span>
                    </div>
                  );
                });
              })()}
              {data.by_industry.length > 12 && (
                <p className="text-[11px] text-muted-foreground pt-2">+ {data.by_industry.length - 12} more industries (top 12 shown)</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {canTeam && team.length > 0 && (
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Team performance</p>
            <div className="space-y-2">
              {team.map((m) => (
                <div key={m.id} className="grid grid-cols-12 items-center gap-3 p-3 rounded-2xl border border-border/60" data-testid={`team-row-${m.id}`}>
                  <div className="col-span-4">
                    <p className="font-semibold text-sm">{m.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.role}</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <p className="font-display text-lg font-extrabold">{m.deals_active}</p>
                    <p className="text-[10px] text-muted-foreground">Active</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <p className="font-display text-lg font-extrabold text-emerald-600">{m.won_count}</p>
                    <p className="text-[10px] text-muted-foreground">Won</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <p className="font-display text-base font-extrabold">{fmtINR(m.won_value)}</p>
                    <p className="text-[10px] text-muted-foreground">Won value</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <p className={`font-display text-lg font-extrabold ${m.overdue_followups > 0 ? "text-red-600" : "text-muted-foreground"}`}>{m.overdue_followups}</p>
                    <p className="text-[10px] text-muted-foreground">Overdue</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
